// shared/config/appearance.ts
//
// Конфигурация внешнего вида витрины («кастомизация»): шаблоны карточек каталога,
// скругления, акцентные цвета, фирменный шрифт.
//
// Хранится в institution_visual.params_json.appearance — бэкенд принимает params_json
// как произвольный объект (UpdateVisualSettingsDto), а get-institution-with-branches
// отдаёт его публичному клиенту. Отдельная таблица и миграция не нужны.
//
// Модуль намеренно чистый: ни стора, ни Vue. Его читают и админка (черновик формы),
// и витрина (через useAppearance), и превью шаблонов — из одного источника правды.

/** Шаблоны карточки товара в каталоге. Порядок = порядок в админке. */
export const CATALOG_CARD_TEMPLATES = ['classic', 'compact', 'overlay', 'row', 'showcase'] as const;
export type CatalogCardTemplate = (typeof CATALOG_CARD_TEMPLATES)[number];

/** Человеческие названия и описания шаблонов — используются в админке и в превью. */
export const CATALOG_CARD_TEMPLATE_META: Record<
  CatalogCardTemplate,
  { title: string; hint: string }
> = {
  classic: {
    title: 'Классическая плитка',
    hint: 'Фото квадратом, название и цена по центру, круглая кнопка «+». Текущий вид сайта.',
  },
  compact: {
    title: 'Компактная',
    hint: 'Текст слева, цена и широкая кнопка в подвале. Помещается больше позиций в экран.',
  },
  overlay: {
    title: 'Фото на всю карточку',
    hint: 'Название и цена поверх фото на градиенте. Витринный вид для крупных фото.',
  },
  row: {
    title: 'Список (строкой)',
    hint: 'Фото слева, название и описание справа. Вид для приложения и узких экранов.',
  },
  showcase: {
    title: 'Крупная витрина',
    hint: 'Широкое фото, крупный заголовок, кнопка во всю ширину. Для хитов и коротких категорий.',
  },
};

/**
 * Комфортная минимальная ширина карточки, px.
 *
 * Настройка «Колонок на широком экране» — это ПОТОЛОК, а не фиксированное число:
 * пять колонок на 4K обязаны превращаться в четыре и три на экранах поменьше, иначе
 * карточки схлопываются в нечитаемые полоски. Порог, на котором каталог отдаёт колонку,
 * задаётся не брейкпоинтом, а вот этой шириной — её сравнивает `max()` в `.pcard-grid`
 * (assets/css/product-cards.scss), и дальше `auto-fill` считает число колонок сам.
 *
 * Значения разные, потому что шаблоны по-разному переносят сжатие: «крупная витрина»
 * с широкой кнопкой ломается раньше, чем компактная карточка с текстом слева.
 * «Список» всегда занимает всю ширину, и минимум ему не нужен.
 */
export const CARD_MIN_WIDTH_PX: Record<CatalogCardTemplate, number> = {
  classic: 200,
  compact: 170,
  overlay: 190,
  row: 0,
  showcase: 280,
};

/** Минимум для плиток категорий — у них нет ни цены, ни кнопки, только фото и название. */
export const CATEGORY_TILE_MIN_WIDTH_PX = 190;

/**
 * Насколько поднимается минимум за каждую колонку сверх трёх.
 *
 * Базовые значения выше — про три колонки, дефолт большинства заведений. Но пять колонок
 * держатся до последнего при том же минимуме: на 1440px карточка ужимается до 256px,
 * и ряд выглядит частоколом. Поэтому чем плотнее заказанная сетка, тем раньше она отдаёт
 * колонку: потерять одну из пяти почти незаметно, а с трёх упасть на две — заметная
 * перемена, и торопиться с ней не надо.
 *
 * Считается от НАСТРОЙКИ, а не от фактического числа колонок в ряду: иначе после первого
 * же шага вниз минимум менялся бы и сетка прыгала между двумя состояниями.
 */
const CARD_MIN_WIDTH_STEP_PX = 40;

const scaleMinWidthPx = (base: number, columnsDesktop: number): number => {
  // «Список» занимает всю ширину при любых настройках — минимум ему не нужен
  if (base === 0) return 0;
  return Math.max(120, base + (columnsDesktop - 3) * CARD_MIN_WIDTH_STEP_PX);
};

/**
 * Минимальная ширина карточки для выбранного шаблона и числа колонок.
 * Ниже неё сетка укладывает в ряд на одну колонку меньше (см. `.pcard-grid`).
 */
export const cardMinWidthPx = (template: CatalogCardTemplate, columnsDesktop: number): number =>
  scaleMinWidthPx(CARD_MIN_WIDTH_PX[template], columnsDesktop);

/** То же для плиток категорий: они делят сетку и настройку колонок с карточками товара. */
export const categoryTileMinWidthPx = (columnsDesktop: number): number =>
  scaleMinWidthPx(CATEGORY_TILE_MIN_WIDTH_PX, columnsDesktop);

/** Стиль скруглений всего интерфейса витрины. */
export const RADIUS_PRESETS = ['round', 'soft', 'square'] as const;
export type RadiusPreset = (typeof RADIUS_PRESETS)[number];

export const RADIUS_PRESET_META: Record<RadiusPreset, { title: string; hint: string }> = {
  round: { title: 'Круглые', hint: 'Максимальные скругления, кнопки-таблетки.' },
  soft: { title: 'Стандартные', hint: 'Умеренные скругления — нейтральный вариант.' },
  square: { title: 'Квадратные', hint: 'Почти прямые углы, строгий вид.' },
};

/** Значения CSS-переменных для каждого пресета скруглений. */
export const RADIUS_PRESET_VALUES: Record<RadiusPreset, { card: string; media: string; control: string }> = {
  round: { card: '24px', media: '16px', control: '9999px' },
  soft: { card: '16px', media: '12px', control: '12px' },
  square: { card: '4px', media: '4px', control: '4px' },
};

/**
 * Обводка карточек и кнопок.
 *
 * Толщина в пикселях; 0 — «не задана»: карточка остаётся с родной волосяной рамкой,
 * у кнопки рамки нет вовсе. Именно так витрина выглядела до появления настройки,
 * поэтому 0 — дефолт, а CSS-переменные при нём вообще не создаются.
 */
export const BORDER_WIDTHS = [0, 1, 2, 3] as const;
export type BorderWidth = (typeof BORDER_WIDTHS)[number];

export const BORDER_WIDTH_META: Record<BorderWidth, { title: string; hint: string }> = {
  0: { title: 'Без обводки', hint: 'Как сейчас: у карточки волосяная рамка, у кнопки её нет.' },
  1: { title: 'Тонкая', hint: '1px — аккуратный контур, читается на светлом фоне.' },
  2: { title: 'Средняя', hint: '2px — заметный контур, «журнальный» вид.' },
  3: { title: 'Жирная', hint: '3px — плакатный контур, работает с квадратными скруглениями.' },
};

/**
 * Обводка применяется к карточкам каталога, плиткам категорий и кнопке «добавить»
 * одним набором значений: разные толщины у соседних элементов выглядят как брак вёрстки.
 */
export interface AppearanceBorderConfig {
  width: BorderWidth;
  /** Цвет обводки. Пусто — обводка не применяется, какой бы ни была толщина. */
  color: string;
}

export const CARD_IMAGE_ASPECTS = ['1:1', '4:3', '3:4', '16:9'] as const;
export type CardImageAspect = (typeof CARD_IMAGE_ASPECTS)[number];

/** CSS-значение для aspect-ratio. */
export const CARD_IMAGE_ASPECT_CSS: Record<CardImageAspect, string> = {
  '1:1': '1 / 1',
  '4:3': '4 / 3',
  '3:4': '3 / 4',
  '16:9': '16 / 9',
};

export const CARD_PRICE_STYLES = ['plain', 'chip'] as const;
export type CardPriceStyle = (typeof CARD_PRICE_STYLES)[number];

export const CARD_ACTION_STYLES = ['icon', 'text', 'icon-text'] as const;
export type CardActionStyle = (typeof CARD_ACTION_STYLES)[number];

export const CARD_SURFACES = ['card', 'plain'] as const;
/** `card` — карточка на подложке с тенью, `plain` — без подложки, только фото и текст. */
export type CardSurface = (typeof CARD_SURFACES)[number];

export const CARD_TEXT_ALIGNS = ['left', 'center'] as const;
export type CardTextAlign = (typeof CARD_TEXT_ALIGNS)[number];

/**
 * Вложенность каталога.
 * `flat` — все категории подряд на одной странице, навигация якорями (так было всегда).
 * `two-level` — сначала плитка категорий, по нажатию проваливаемся внутрь.
 */
export const CATALOG_NESTINGS = ['flat', 'two-level'] as const;
export type CatalogNesting = (typeof CATALOG_NESTINGS)[number];

export const CATALOG_NESTING_META: Record<CatalogNesting, { title: string; hint: string }> = {
  flat: {
    title: 'Без вложенности',
    hint: 'Все категории подряд, переход по ним — прокруткой. Хорошо для короткого меню.',
  },
  'two-level': {
    title: 'С вложенностью',
    hint: 'Сначала плитка категорий, внутрь проваливаемся по нажатию. Спасает большое меню от бесконечной прокрутки.',
  },
};

/** Подложка под названием на карточке категории. */
export const CATEGORY_LABEL_SURFACES = ['dark', 'light', 'none'] as const;
export type CategoryLabelSurface = (typeof CATEGORY_LABEL_SURFACES)[number];

/** Настройки каталога для одной среды — сайта или приложения. */
export interface AppearanceCatalogConfig {
  template: CatalogCardTemplate;
  /** Колонок на широком экране (для `row` игнорируется — строка всегда во всю ширину). */
  columnsDesktop: 2 | 3 | 4 | 5;
  /** Колонок на телефоне. */
  columnsMobile: 1 | 2;
  imageAspect: CardImageAspect;
  surface: CardSurface;
  textAlign: CardTextAlign;
  priceStyle: CardPriceStyle;
  actionStyle: CardActionStyle;
  showDescription: boolean;
  showBadges: boolean;
  showFavorite: boolean;
  /** Плоский каталог или плитка категорий с переходом внутрь. */
  nesting: CatalogNesting;
  /** Подложка под названием на карточке категории (только для `two-level`). */
  categoryLabelSurface: CategoryLabelSurface;
  /** Непрозрачность подложки лейбла, проценты 0–100. */
  categoryLabelOpacity: number;
}

/**
 * Макеты обложки — первого экрана сайта.
 * `none` — обложки нет, сайт открывается сразу каталогом (так было до появления настройки).
 */
export const COVER_LAYOUTS = ['none', 'hero', 'logo', 'title', 'photo', 'media', 'split', 'video'] as const;
export type CoverLayout = (typeof COVER_LAYOUTS)[number];

export const COVER_LAYOUT_META: Record<CoverLayout, { title: string; hint: string }> = {
  none: {
    title: 'Без обложки',
    hint: 'Сайт открывается сразу каталогом. Так работает сейчас.',
  },
  hero: {
    title: 'Баннер во весь экран',
    hint: 'Фото или ролик от края до края и почти во всю высоту первого экрана, без текста и затемнения. Шапку лучше сделать прозрачной — она ляжет поверх.',
  },
  logo: {
    title: 'Только логотип',
    hint: 'Логотип по центру на фоне обложки. Минимализм — если бренд узнают по знаку.',
  },
  title: {
    title: 'Заголовок по центру',
    hint: 'Текст без фото, фон — акцентный градиент. Работает, даже когда снимков нет.',
  },
  photo: {
    title: 'Фото и заголовок',
    hint: 'Фото на всю ширину, текст по центру поверх затемнения.',
  },
  media: {
    title: 'Только картинка',
    hint: 'Фото или видео без текста и затемнения — когда весь смысл нарисован на самом баннере.',
  },
  split: {
    title: 'Половина текст, половина фото',
    hint: 'Слева заголовок и описание, справа фото. На телефоне — друг под другом.',
  },
  video: {
    title: 'Видео',
    hint: 'Зацикленный ролик без звука на фоне, текст поверх. Загружается в то же поле «Фон обложки».',
  },
};

export const COVER_HEIGHTS = ['compact', 'medium', 'tall', 'half', 'screen'] as const;
export type CoverHeight = (typeof COVER_HEIGHTS)[number];

/**
 * Первый экран как единица измерения.
 *
 * `screen` — ровно первый экран и ни пикселем больше, `half` — его половина.
 *
 * Не `100dvh` и не `50dvh`: обложка не всегда начинается с верха страницы. В контейнере
 * она стоит ниже фиксированной шапки и своего отступа, и голые `100dvh` увели бы её низ
 * за край экрана. `--app-cover-top` — фактическое расстояние от верха вьюпорта до обложки;
 * его считают scoped-стили слота (widgets/site-cover/ui/SiteCover.vue) теми же слагаемыми,
 * какими задают отступ сверху, поэтому низ обложки всегда попадает куда обещано.
 *
 * `dvh`, а не `vh`: на телефоне адресная строка то появляется, то прячется, и с `vh`
 * баннер оказывался бы то короче экрана, то длиннее.
 *
 * Эти две ступени намеренно НЕ зажаты в px, в отличие от трёх качественных выше: там
 * «средняя» — это про вид, а здесь «половина экрана» — это ровно половина экрана.
 * Из-за этого при разной высоте окна они могут оказаться то выше «Высокой», то ниже —
 * поэтому и подписаны точной величиной, а не ступенью размера.
 */
const COVER_HEIGHT_SCREEN = 'calc(100dvh - var(--app-cover-top, 0px))';
const COVER_HEIGHT_HALF = 'calc((100dvh - var(--app-cover-top, 0px)) / 2)';

/** Высота обложки в CSS. Ограничение по vh — чтобы на ноутбуке каталог оставался виден. */
export const COVER_HEIGHT_CSS: Record<CoverHeight, string> = {
  compact: 'clamp(180px, 26vh, 260px)',
  medium: 'clamp(260px, 42vh, 420px)',
  tall: 'clamp(340px, 62vh, 640px)',
  half: COVER_HEIGHT_HALF,
  screen: COVER_HEIGHT_SCREEN,
};

/**
 * Своя лестница высот для макета «Баннер во весь экран».
 *
 * Обычные значения ему малы: обложка в контейнере делит первый экран с каталогом,
 * а этот баннер сам и есть первый экран. Верхняя граница у «высокой» не убрана совсем —
 * на длинном мониторе баннер во всю высоту прячет каталог так, что до него
 * не догадаешься прокрутить. Кому нужен именно такой первый экран — есть «во весь экран».
 */
export const HERO_HEIGHT_CSS: Record<CoverHeight, string> = {
  compact: 'clamp(320px, 46vh, 520px)',
  medium: 'clamp(420px, 66vh, 780px)',
  tall: 'clamp(520px, 88vh, 1100px)',
  // Половина и весь экран — величины абсолютные, своей «геройской» версии у них нет
  half: COVER_HEIGHT_HALF,
  screen: COVER_HEIGHT_SCREEN,
};

/**
 * Ширина обложки.
 * `container` — обложка живёт в колонке контента, как весь остальной каталог (так было всегда).
 * `full` — растянута от края до края экрана, поверх отступов контейнера; текст внутри
 * при этом остаётся выровненным по контенту, иначе на широком мониторе он уезжает к рамке.
 */
export const COVER_WIDTHS = ['container', 'full'] as const;
export type CoverWidth = (typeof COVER_WIDTHS)[number];

export const COVER_WIDTH_META: Record<CoverWidth, { title: string; hint: string }> = {
  container: {
    title: 'В контейнере',
    hint: 'По ширине каталога, со скруглением. Так обложка выглядит сейчас.',
  },
  full: {
    title: 'На всю ширину экрана',
    hint: 'От края до края, без скруглений — как хедер-баннер на промо-сайтах.',
  },
};

/**
 * Макеты, которые растянуты на всю ширину экрана независимо от `cover.width`.
 * «Баннер во весь экран» — это и есть его определение, отдельный переключатель
 * ширины там только сбивал бы с толку.
 */
export const FULL_BLEED_COVER_LAYOUTS: readonly CoverLayout[] = ['hero'];

export interface AppearanceCoverConfig {
  layout: CoverLayout;
  /** По ширине контента или во весь экран. Макеты из FULL_BLEED_COVER_LAYOUTS игнорируют. */
  width: CoverWidth;
  /** Заголовок на обложке. Пусто — подставляется название заведения. */
  title: string;
  subtitle: string;
  /**
   * Легаси-поле: раньше видео обложки загружалось отдельно от картинки.
   * Сейчас и то и другое лежит в visual.banner_img, а тег выбирается по
   * расширению файла. Поле оставлено, чтобы уже сохранённые ролики
   * продолжали играть; форма в админке его больше не заполняет.
   */
  videoUrl: string;
  /** Затемнение поверх медиа, проценты 0–80: без него текст на светлом фото не читается. */
  overlay: number;
  height: CoverHeight;
}

/**
 * Растянута ли обложка от края до края экрана.
 *
 * «От края до края» — это и по горизонтали, и по вертикали: баннер начинается с самого
 * верха страницы и уходит ПОД шапку, а не подставляется под неё. Иначе над ним остаётся
 * полоса фона высотой в шапку — ровно то, из-за чего вид разъезжался с эталоном.
 *
 * Отсюда же следует прозрачная шапка: непрозрачная полоса поверх такого баннера
 * отменяет весь смысл затеи. Поэтому условие живёт в общем месте — его спрашивают
 * и обложка (куда себя сдвинуть), и шапка (какой быть).
 */
export const coverStretchesFullBleed = (
  cover: AppearanceCoverConfig | undefined | null,
): boolean => {
  if (!cover || cover.layout === 'none') return false;
  return cover.width === 'full' || FULL_BLEED_COVER_LAYOUTS.includes(cover.layout);
};

/**
 * Ориентация плиток новостного слайдера (сторисов).
 * `vertical` — высокие плитки как в сторис, много в ряд; `horizontal` — широкие
 * баннеры 16:9, по 1–3 в ряд.
 */
export const BANNER_ORIENTATIONS = ['vertical', 'horizontal'] as const;
export type BannerOrientation = (typeof BANNER_ORIENTATIONS)[number];

export interface AppearanceBannersConfig {
  orientation: BannerOrientation;
}

/**
 * Сколько плиток слайдера влезает в ряд. Ключи — ширина экрана.
 * Горизонтальные баннеры крупнее, поэтому их в ряд помещается втрое меньше.
 */
export const BANNER_BREAKPOINTS: Record<BannerOrientation, Record<number, { slidesPerView: number; spaceBetween: number }>> = {
  vertical: {
    320: { slidesPerView: 2, spaceBetween: 10 },
    360: { slidesPerView: 2.5, spaceBetween: 10 },
    380: { slidesPerView: 3, spaceBetween: 10 },
    480: { slidesPerView: 3.6, spaceBetween: 10 },
    850: { slidesPerView: 4, spaceBetween: 15 },
    1024: { slidesPerView: 5, spaceBetween: 15 },
    1850: { slidesPerView: 5.6, spaceBetween: 15 },
  },
  horizontal: {
    320: { slidesPerView: 1.15, spaceBetween: 10 },
    480: { slidesPerView: 1.4, spaceBetween: 10 },
    850: { slidesPerView: 2.2, spaceBetween: 15 },
    1024: { slidesPerView: 3, spaceBetween: 15 },
    1850: { slidesPerView: 3.4, spaceBetween: 15 },
  },
};

/**
 * Блоки контентной страницы.
 * Набор намеренно маленький: четырёх типов хватает на «О нас», «Команду» и «Хиты недели»,
 * а каждый лишний тип — это ещё одна форма в админке и ещё один способ сломать вёрстку.
 */
export const CONTENT_BLOCK_TYPES = ['heading', 'text', 'image', 'cards'] as const;
export type ContentBlockType = (typeof CONTENT_BLOCK_TYPES)[number];

export const CONTENT_BLOCK_META: Record<ContentBlockType, { title: string; hint: string }> = {
  heading: { title: 'Заголовок', hint: 'Крупный текст — разделитель смысловых частей страницы.' },
  text: { title: 'Абзац', hint: 'Обычный текст. Переносы строк сохраняются.' },
  image: { title: 'Картинка', hint: 'Фото во всю ширину с подписью под ним.' },
  cards: { title: 'Карточки', hint: 'Сетка «фото + заголовок + текст»: команда, хиты, преимущества.' },
};

/** Карточка внутри блока `cards`. */
export interface ContentCard {
  imageUrl: string;
  title: string;
  text: string;
}

export interface ContentBlock {
  type: ContentBlockType;
  /** Заголовок (heading), подпись к картинке (image), заголовок сетки (cards). */
  title: string;
  /** Текст абзаца. */
  text: string;
  /** Картинка блока `image`. */
  imageUrl: string;
  /** Карточки блока `cards`. */
  cards: ContentCard[];
}

export interface ContentPage {
  /** Адрес страницы: /p/<slug>. Только латиница, цифры и дефис. */
  slug: string;
  title: string;
  /** Черновик не отдаётся гостям, но остаётся в админке. */
  published: boolean;
  blocks: ContentBlock[];
}

/** Больше десяти страниц — это уже сайт, а не пара информационных страниц. */
export const CONTENT_PAGES_LIMIT = 10;
export const CONTENT_BLOCKS_LIMIT = 30;
export const CONTENT_CARDS_LIMIT = 24;

/** Адрес страницы на витрине. */
export const contentPagePath = (slug: string): string => `/p/${slug}`;

/**
 * Фирменный фон витрины.
 *
 * Пусто — фон остаётся системным (белый в светлой теме, почти чёрный в тёмной).
 * Два поля, а не одно: цвет, который хорош на светлой теме, на тёмной обычно слепит,
 * и наоборот. Заполнить можно только одно — второе останется системным.
 */
export interface AppearanceBackgroundConfig {
  light: string;
  dark: string;
  /**
   * Красить ли фирменным фоном ещё и «обвязку» витрины: шапку, липкую строку фильтров
   * и панель корзины. Выключено — они остаются системно-белыми (так фон работал раньше),
   * и включённый фирменный фон виден только под каталогом.
   *
   * Отдельный флаг, а не всегда: у заведений, которые уже задали фон, шапка сегодня белая,
   * и перекрасить её без спроса — заметное изменение витрины, а не починка.
   */
  applyToChrome: boolean;
}

/**
 * Раскладка главной: боковое меню категорий, каталог и корзина.
 *
 * Пороги ширины экрана зашиты в CSS (медиазапросы нельзя собрать из настройки на лету),
 * поэтому вместо чисел — три готовых пресета.
 */
export const LAYOUT_PRESETS = ['wide', 'dense', 'single'] as const;
export type LayoutPreset = (typeof LAYOUT_PRESETS)[number];

export const LAYOUT_PRESET_META: Record<LayoutPreset, { title: string; hint: string }> = {
  wide: {
    title: 'Широкая',
    hint: 'Корзина-колонка с 1280px, боковое меню с 1536px. Так витрина работает сейчас.',
  },
  dense: {
    title: 'Плотная',
    hint: 'Корзина-колонка с 1024px, боковое меню с 1280px — на обычном ноутбуке видно все три колонки.',
  },
  single: {
    title: 'Без боковых колонок',
    hint: 'Каталог во всю ширину на любом экране: бокового меню нет, корзина открывается шторкой по кнопке.',
  },
};

/**
 * С какой ширины экрана корзина перестаёт быть шторкой и становится колонкой.
 * `null` — не становится никогда (пресет «Без боковых колонок»).
 *
 * Число живёт здесь, потому что его должны знать двое: медиазапросы
 * (assets/css/appearance-layout.scss) и JS, который считает высоту липкой колонки
 * (widgets/basket-mobile). Разъехавшись, они дают провал под кнопкой «Оформить заказ»:
 * CSS растягивает шторку во весь экран, а JS вешает на неё высоту колонки.
 */
export const LAYOUT_BASKET_COLUMN_BREAKPOINT: Record<LayoutPreset, number | null> = {
  wide: 1280,
  dense: 1024,
  single: null,
};

export interface AppearanceLayoutConfig {
  grid: LayoutPreset;
}

/** Где стоит логотип в шапке сайта. */
export const HEADER_LOGO_POSITIONS = ['left', 'center', 'hidden'] as const;
export type HeaderLogoPosition = (typeof HEADER_LOGO_POSITIONS)[number];

export const HEADER_LOGO_META: Record<HeaderLogoPosition, { title: string; hint: string }> = {
  left: { title: 'Слева', hint: 'Логотип в строке с поиском и контактами. Так работает сейчас.' },
  center: { title: 'По центру', hint: 'Логотип отдельной строкой над поиском и контактами.' },
  hidden: { title: 'Скрыть', hint: 'Логотипа в шапке нет — например, если он уже на обложке.' },
};

/** Подложка шапки. */
export const HEADER_SURFACES = ['solid', 'transparent'] as const;
export type HeaderSurface = (typeof HEADER_SURFACES)[number];

/** Дополнительная ссылка в шапке и в мобильном меню. */
export interface AppearanceHeaderLink {
  label: string;
  href: string;
}

/** Больше шести ссылок в шапку не помещается — дальше начинается горизонтальная прокрутка. */
export const HEADER_LINKS_LIMIT = 6;

export interface AppearanceHeaderConfig {
  logo: HeaderLogoPosition;
  /**
   * `transparent` — шапка без фона поверх обложки, пока страница не прокручена.
   * Прокрутили — фон возвращается, иначе текст шапки ложится на каталог и не читается.
   */
  surface: HeaderSurface;
  links: AppearanceHeaderLink[];
}

/** Настройки экрана товара — общие для сайта и приложения. */
export interface AppearanceProductCardConfig {
  /** Показывать картинки у добавок/топпингов. Выключено — компактный список без фото. */
  toppingsWithImages: boolean;
}

export interface AppearanceBrandFont {
  enabled: boolean;
  /** font-family, как его понимает CSS: `'Involve', sans-serif`. */
  family: string;
  /** Ссылка на CSS со шрифтом (Google Fonts / свой @font-face). Пусто — шрифт уже в сборке. */
  cssUrl: string;
}

export interface AppearanceConfig {
  /** Версия схемы. Растёт только при несовместимых изменениях — normalize это учитывает. */
  version: 1;
  radius: RadiusPreset;
  /** Акцент кнопки «добавить» в каталоге и в карточке товара. */
  accentColor: string;
  /** Цвет той же кнопки, когда товар уже в корзине. */
  accentInCartColor: string;
  /** Заливка зоны доставки на карте. */
  deliveryZoneColor: string;
  /** Заливка баннера «Получать бонусы и скидки» — его видит только гость без входа. */
  loyaltyBannerColor: string;
  /**
   * Цвет текста того же баннера. Пусто — считается по контрасту с заливкой
   * (чёрный или белый, что читается). Задан — берётся как есть: автоподбор знает
   * только два цвета, а бренду бывает нужен свой.
   */
  loyaltyBannerTextColor: string;
  /** Обводка карточек каталога, плиток категорий и кнопки «добавить». */
  border: AppearanceBorderConfig;
  brandFont: AppearanceBrandFont;
  catalog: {
    site: AppearanceCatalogConfig;
    app: AppearanceCatalogConfig;
  };
  productCard: AppearanceProductCardConfig;
  /** Новостной слайдер (сторисы) — вертикальные плитки или широкие баннеры. */
  banners: AppearanceBannersConfig;
  /** Обложка — настройка сайта: в приложении из сторов не показывается. */
  cover: AppearanceCoverConfig;
  /** Шапка сайта: логотип, подложка, дополнительные ссылки. */
  header: AppearanceHeaderConfig;
  /** Контентные страницы «О нас», «Команда», акции. */
  pages: ContentPage[];
  /** Фирменный фон витрины. */
  background: AppearanceBackgroundConfig;
  /** Раскладка главной: пороги, на которых появляются боковое меню и корзина-колонка. */
  layout: AppearanceLayoutConfig;
}

/** Среда отображения каталога. */
export type AppearanceSurfaceKey = 'site' | 'app';

/**
 * Дефолты подобраны так, чтобы витрина без сохранённого конфига выглядела
 * ровно как сегодня: классическая плитка, 3 колонки, квадратное фото, кнопка-иконка.
 * Любое изменение дефолта здесь меняет вид у всех заведений, где админ ничего не трогал.
 */
export const DEFAULT_CATALOG_CONFIG: AppearanceCatalogConfig = {
  template: 'classic',
  columnsDesktop: 3,
  columnsMobile: 2,
  imageAspect: '1:1',
  surface: 'card',
  textAlign: 'center',
  priceStyle: 'plain',
  actionStyle: 'icon',
  showDescription: false,
  showBadges: true,
  showFavorite: true,
  // Плоский каталог — так витрина работала всегда
  nesting: 'flat',
  categoryLabelSurface: 'dark',
  categoryLabelOpacity: 55,
};

export const DEFAULT_APPEARANCE: AppearanceConfig = {
  version: 1,
  radius: 'round',
  accentColor: '#EF4444',
  accentInCartColor: '#16A34A',
  deliveryZoneColor: '#EF4444',
  // Тот же жёлтый, что был зашит в баннер классом bg-yellow-300 — вид не меняется,
  // пока владелец не выберет свой цвет.
  loyaltyBannerColor: '#FDE047',
  // Пусто — цвет текста подбирается по контрасту, как было с зашитым text-black
  loyaltyBannerTextColor: '',
  // Обводки нет: карточке хватает родной волосяной рамки, у кнопки её никогда не было
  border: { width: 0, color: '' },
  brandFont: { enabled: false, family: '', cssUrl: '' },
  catalog: {
    site: { ...DEFAULT_CATALOG_CONFIG },
    app: { ...DEFAULT_CATALOG_CONFIG },
  },
  // Картинки добавок показываем — так было до появления настройки
  productCard: { toppingsWithImages: true },
  // Вертикальные плитки — так слайдер выглядел до появления настройки
  banners: { orientation: 'vertical' },
  // Обложки по умолчанию нет: сайт как открывался каталогом, так и открывается
  cover: {
    layout: 'none',
    width: 'container',
    title: '',
    subtitle: '',
    videoUrl: '',
    overlay: 40,
    height: 'medium',
  },
  // Шапка как была: логотип слева, с подложкой, без дополнительных ссылок
  header: { logo: 'left', surface: 'solid', links: [] },
  // Контентных страниц по умолчанию нет
  pages: [],
  // Фон системный: белый в светлой теме, почти чёрный в тёмной; обвязка не красится
  background: { light: '', dark: '', applyToChrome: false },
  // Пороги колонок как были: корзина с 1280px, боковое меню с 1536px
  layout: { grid: 'wide' },
};

/** Ключ внутри params_json, под которым лежит конфиг. */
export const APPEARANCE_PARAMS_KEY = 'appearance';

const HEX_COLOR = /^#(?:[\dA-Fa-f]{3}|[\dA-Fa-f]{6})$/;

const pickEnum = <T extends readonly string[]>(
  allowed: T,
  value: unknown,
  fallback: T[number],
): T[number] => (allowed.includes(value as T[number]) ? (value as T[number]) : fallback);

const pickColor = (value: unknown, fallback: string): string =>
  typeof value === 'string' && HEX_COLOR.test(value.trim()) ? value.trim() : fallback;

const pickBool = (value: unknown, fallback: boolean): boolean =>
  typeof value === 'boolean' ? value : fallback;

const pickNumber = <T extends number>(allowed: readonly T[], value: unknown, fallback: T): T => {
  const parsed = Number(value);
  return (allowed as readonly number[]).includes(parsed) ? (parsed as T) : fallback;
};

/** Проценты 0–100. Значение вне диапазона зажимаем, мусор заменяем дефолтом. */
const pickPercent = (value: unknown, fallback: number): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.min(100, Math.max(0, Math.round(parsed))) : fallback;
};

const normalizeCatalog = (raw: unknown, fallback: AppearanceCatalogConfig): AppearanceCatalogConfig => {
  const source = (raw ?? {}) as Partial<AppearanceCatalogConfig>;
  return {
    template: pickEnum(CATALOG_CARD_TEMPLATES, source.template, fallback.template),
    columnsDesktop: pickNumber([2, 3, 4, 5] as const, source.columnsDesktop, fallback.columnsDesktop),
    columnsMobile: pickNumber([1, 2] as const, source.columnsMobile, fallback.columnsMobile),
    imageAspect: pickEnum(CARD_IMAGE_ASPECTS, source.imageAspect, fallback.imageAspect),
    surface: pickEnum(CARD_SURFACES, source.surface, fallback.surface),
    textAlign: pickEnum(CARD_TEXT_ALIGNS, source.textAlign, fallback.textAlign),
    priceStyle: pickEnum(CARD_PRICE_STYLES, source.priceStyle, fallback.priceStyle),
    actionStyle: pickEnum(CARD_ACTION_STYLES, source.actionStyle, fallback.actionStyle),
    showDescription: pickBool(source.showDescription, fallback.showDescription),
    showBadges: pickBool(source.showBadges, fallback.showBadges),
    showFavorite: pickBool(source.showFavorite, fallback.showFavorite),
    nesting: pickEnum(CATALOG_NESTINGS, source.nesting, fallback.nesting),
    categoryLabelSurface: pickEnum(
      CATEGORY_LABEL_SURFACES,
      source.categoryLabelSurface,
      fallback.categoryLabelSurface,
    ),
    categoryLabelOpacity: pickPercent(source.categoryLabelOpacity, fallback.categoryLabelOpacity),
  };
};

const pickText = (value: unknown, maxLength: number): string =>
  typeof value === 'string' ? value.trim().slice(0, maxLength) : '';

const normalizeCover = (raw: unknown): AppearanceCoverConfig => {
  const source = (raw ?? {}) as Partial<AppearanceCoverConfig>;
  const overlay = Number(source.overlay);
  return {
    layout: pickEnum(COVER_LAYOUTS, source.layout, DEFAULT_APPEARANCE.cover.layout),
    width: pickEnum(COVER_WIDTHS, source.width, DEFAULT_APPEARANCE.cover.width),
    // Длину режем здесь, а не в форме: конфиг правится и руками через «Params JSON»,
    // а обложка с полотном текста ломает вёрстку первого экрана.
    title: pickText(source.title, 120),
    subtitle: pickText(source.subtitle, 240),
    videoUrl: pickText(source.videoUrl, 1000),
    overlay: Number.isFinite(overlay) ? Math.min(80, Math.max(0, Math.round(overlay))) : DEFAULT_APPEARANCE.cover.overlay,
    height: pickEnum(COVER_HEIGHTS, source.height, DEFAULT_APPEARANCE.cover.height),
  };
};

/**
 * Обводка.
 *
 * Толщина без цвета — не обводка, а невидимая рамка, которая только съедает пиксели
 * под контентом, поэтому одно пустое поле гасит второе.
 */
const normalizeBorder = (raw: unknown): AppearanceBorderConfig => {
  const source = (raw ?? {}) as Partial<AppearanceBorderConfig>;
  const color = pickColor(source.color, '');
  const width = pickNumber(BORDER_WIDTHS, source.width, DEFAULT_APPEARANCE.border.width);
  return color && width > 0 ? { width, color } : { ...DEFAULT_APPEARANCE.border };
};

const normalizeHeader = (raw: unknown): AppearanceHeaderConfig => {
  const source = (raw ?? {}) as Partial<AppearanceHeaderConfig>;
  const rawLinks = Array.isArray(source.links) ? source.links : [];

  return {
    logo: pickEnum(HEADER_LOGO_POSITIONS, source.logo, DEFAULT_APPEARANCE.header.logo),
    surface: pickEnum(HEADER_SURFACES, source.surface, DEFAULT_APPEARANCE.header.surface),
    // Ссылка без подписи или без адреса — мусор, который в шапке выглядел бы дырой
    links: rawLinks
      .map((link) => ({
        label: pickText((link as Partial<AppearanceHeaderLink>)?.label, 40),
        href: pickText((link as Partial<AppearanceHeaderLink>)?.href, 500),
      }))
      .filter((link) => link.label && link.href)
      .slice(0, HEADER_LINKS_LIMIT),
  };
};

/** Slug страницы: латиница, цифры, дефис. Всё остальное режем — из него строится адрес. */
const normalizeSlug = (value: unknown): string =>
  pickText(value, 60)
    .toLowerCase()
    .replaceAll(/[^\da-z-]/g, '-')
    .replaceAll(/-{2,}/g, '-')
    .replaceAll(/^-|-$/g, '');

const normalizeCard = (raw: unknown): ContentCard => {
  const source = (raw ?? {}) as Partial<ContentCard>;
  return {
    imageUrl: pickText(source.imageUrl, 1000),
    title: pickText(source.title, 120),
    text: pickText(source.text, 600),
  };
};

const normalizeBlock = (raw: unknown): ContentBlock => {
  const source = (raw ?? {}) as Partial<ContentBlock>;
  return {
    type: pickEnum(CONTENT_BLOCK_TYPES, source.type, 'text'),
    title: pickText(source.title, 200),
    // Текст абзаца длинный по природе, но не бесконечный: страница на 20 000 знаков —
    // это уже не «о нас», а способ уронить вёрстку
    text: pickText(source.text, 5000),
    imageUrl: pickText(source.imageUrl, 1000),
    cards: (Array.isArray(source.cards) ? source.cards : []).map(normalizeCard).slice(0, CONTENT_CARDS_LIMIT),
  };
};

/**
 * Контентные страницы из params_json.
 *
 * Страница без slug или без заголовка выбрасывается: по такой всё равно не перейти.
 * Дубликаты slug тоже — иначе два разных содержимого претендуют на один адрес,
 * и какое откроется, зависит от порядка в массиве.
 */
const normalizePages = (raw: unknown): ContentPage[] => {
  const list = Array.isArray(raw) ? raw : [];
  const seen = new Set<string>();

  return list
    .map((page) => {
      const source = (page ?? {}) as Partial<ContentPage>;
      return {
        slug: normalizeSlug(source.slug),
        title: pickText(source.title, 120),
        published: pickBool(source.published, false),
        blocks: (Array.isArray(source.blocks) ? source.blocks : [])
          .map(normalizeBlock)
          .slice(0, CONTENT_BLOCKS_LIMIT),
      };
    })
    .filter((page) => {
      if (!page.slug || !page.title || seen.has(page.slug)) return false;
      seen.add(page.slug);
      return true;
    })
    .slice(0, CONTENT_PAGES_LIMIT);
};

/**
 * Приводит что угодно из params_json к валидному конфигу.
 *
 * Конфиг редактируется руками через «Params JSON» в админке и приезжает с сервера как `any`,
 * поэтому доверять форме нельзя: любое неизвестное значение молча заменяется дефолтом.
 * Падать на кривом JSON нельзя — это витрина, а не админка.
 */
export const normalizeAppearance = (raw: unknown): AppearanceConfig => {
  const source = (raw ?? {}) as Partial<AppearanceConfig>;
  const catalog = (source.catalog ?? {}) as Partial<AppearanceConfig['catalog']>;
  const site = normalizeCatalog(catalog.site, DEFAULT_APPEARANCE.catalog.site);
  const font = (source.brandFont ?? {}) as Partial<AppearanceBrandFont>;

  return {
    version: 1,
    radius: pickEnum(RADIUS_PRESETS, source.radius, DEFAULT_APPEARANCE.radius),
    accentColor: pickColor(source.accentColor, DEFAULT_APPEARANCE.accentColor),
    accentInCartColor: pickColor(source.accentInCartColor, DEFAULT_APPEARANCE.accentInCartColor),
    deliveryZoneColor: pickColor(source.deliveryZoneColor, DEFAULT_APPEARANCE.deliveryZoneColor),
    loyaltyBannerColor: pickColor(
      source.loyaltyBannerColor,
      DEFAULT_APPEARANCE.loyaltyBannerColor,
    ),
    // Пустая строка — валидное значение: означает «подбери по контрасту»
    loyaltyBannerTextColor: pickColor(source.loyaltyBannerTextColor, ''),
    border: normalizeBorder(source.border),
    brandFont: {
      enabled: pickBool(font.enabled, DEFAULT_APPEARANCE.brandFont.enabled),
      family: typeof font.family === 'string' ? font.family.trim() : '',
      cssUrl: typeof font.cssUrl === 'string' ? font.cssUrl.trim() : '',
    },
    catalog: {
      site,
      // Приложение по умолчанию наследует сайт: заведению редко нужны два разных каталога,
      // а «пустой» app-конфиг иначе откатывался бы к глобальному дефолту, а не к тому, что видно на сайте.
      app: normalizeCatalog(catalog.app, site),
    },
    productCard: {
      toppingsWithImages: pickBool(
        (source.productCard as Partial<AppearanceProductCardConfig> | undefined)?.toppingsWithImages,
        DEFAULT_APPEARANCE.productCard.toppingsWithImages,
      ),
    },
    cover: normalizeCover(source.cover),
    header: normalizeHeader(source.header),
    pages: normalizePages(source.pages),
    background: {
      // Пустая строка — «оставить системный», поэтому невалидный цвет тоже гасим в пустоту,
      // а не подставляем чужой: неожиданно перекрашенная витрина хуже, чем неприменённая настройка
      light: pickColor((source.background as Partial<AppearanceBackgroundConfig> | undefined)?.light, ''),
      dark: pickColor((source.background as Partial<AppearanceBackgroundConfig> | undefined)?.dark, ''),
      applyToChrome: pickBool(
        (source.background as Partial<AppearanceBackgroundConfig> | undefined)?.applyToChrome,
        DEFAULT_APPEARANCE.background.applyToChrome,
      ),
    },
    layout: {
      grid: pickEnum(
        LAYOUT_PRESETS,
        (source.layout as Partial<AppearanceLayoutConfig> | undefined)?.grid,
        DEFAULT_APPEARANCE.layout.grid,
      ),
    },
    banners: {
      orientation: pickEnum(
        BANNER_ORIENTATIONS,
        (source.banners as Partial<AppearanceBannersConfig> | undefined)?.orientation,
        DEFAULT_APPEARANCE.banners.orientation,
      ),
    },
  };
};

/** Достаёт конфиг из visual.params_json (в любом его виде) и нормализует. */
export const readAppearanceFromParams = (params: unknown): AppearanceConfig =>
  normalizeAppearance((params as Record<string, unknown> | null | undefined)?.[APPEARANCE_PARAMS_KEY]);

/** Кладёт конфиг обратно в params_json, не затирая соседние ключи (промо-картинки, QR). */
export const writeAppearanceToParams = (
  params: Record<string, unknown> | null | undefined,
  appearance: AppearanceConfig,
): Record<string, unknown> => ({
  ...params,
  [APPEARANCE_PARAMS_KEY]: appearance,
});

/**
 * CSS-переменные витрины. Вешаются на <html> плагином и на контейнер превью в админке —
 * поэтому одна и та же карточка одинаково выглядит и на сайте, и в настройках.
 */
export const appearanceCssVars = (config: AppearanceConfig): Record<string, string> => {
  const radius = RADIUS_PRESET_VALUES[config.radius];
  const vars: Record<string, string> = {
    '--app-radius-card': radius.card,
    '--app-radius-media': radius.media,
    '--app-radius-control': radius.control,
    '--app-accent': config.accentColor,
    '--app-accent-foreground': contrastTextColor(config.accentColor),
    '--app-accent-incart': config.accentInCartColor,
    '--app-accent-incart-foreground': contrastTextColor(config.accentInCartColor),
    '--app-delivery-zone': config.deliveryZoneColor,
    '--app-loyalty-banner': config.loyaltyBannerColor,
    // Заданный цвет побеждает автоподбор. Переменная создаётся всегда: без неё
    // текст унаследовал бы цвет страницы и мог утонуть в заливке.
    '--app-loyalty-banner-foreground':
      config.loyaltyBannerTextColor || contrastTextColor(config.loyaltyBannerColor),
  };
  if (config.brandFont.enabled && config.brandFont.family) {
    vars['--app-brand-font'] = config.brandFont.family;
  }
  // Фон витрины: переменную создаём только если цвет задан. Пустая переменная
  // сделала бы `background-color` невалидным, и фон стал бы прозрачным вместо системного.
  if (config.background.light) vars['--app-page-bg-light'] = config.background.light;
  if (config.background.dark) vars['--app-page-bg-dark'] = config.background.dark;
  // Обвязка (шапка, фильтры, панель корзины) красится ОТДЕЛЬНОЙ парой переменных.
  // У неё, в отличие от подложки витрины, есть свой непрозрачный цвет по умолчанию:
  // если бы CSS читал те же --app-page-bg-*, включённый фон витрины автоматически
  // перекрасил бы и шапку — а это уже другое решение, и принимает его админ галочкой.
  if (config.background.applyToChrome) {
    if (config.background.light) vars['--app-chrome-bg-light'] = config.background.light;
    if (config.background.dark) vars['--app-chrome-bg-dark'] = config.background.dark;
  }
  // Обводка: переменных нет, пока не заданы и толщина, и цвет — тогда карточки и кнопки
  // остаются на своих fallback-значениях, то есть выглядят как до появления настройки.
  if (config.border.width > 0 && config.border.color) {
    vars['--app-border-width'] = `${config.border.width}px`;
    vars['--app-border-color'] = config.border.color;
  }
  return vars;
};

/** Чёрный или белый текст — тот, что читается на переданном фоне. */
export const contrastTextColor = (hex: string): string => {
  const normalized = hex.replace('#', '');
  const full =
    normalized.length === 3
      ? [...normalized].map((char) => char + char).join('')
      : normalized;
  const r = Number.parseInt(full.slice(0, 2), 16);
  const g = Number.parseInt(full.slice(2, 4), 16);
  const b = Number.parseInt(full.slice(4, 6), 16);
  if ([r, g, b].some(Number.isNaN)) return '#FFFFFF';
  // Стандартная формула воспринимаемой яркости (ITU-R BT.601).
  return (r * 299 + g * 587 + b * 114) / 1000 > 140 ? '#111111' : '#FFFFFF';
};
